<template>
  <tr
    :key="item.id"
    class="tw-border-b tw-border-solid tw-border-gray-100 last:tw-border-b-0 tw-group even:tw-bg-gray-50/50 hover:tw-bg-primary-50/60 tw-duration-100"
  >
    <!-- ID -->
    <td class="tw-px-5 tw-py-3 tw-text-sm">{{ item.id }}</td>

    <!-- Video (thumbnail + title) -->
    <td class="tw-px-5 tw-py-3 tw-text-sm">
      <div class="tw-flex tw-items-center tw-gap-3">
        <div class="tw-w-20 tw-h-12 tw-rounded tw-bg-gray-100 tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-shrink-0">
          <img v-if="item.thumbnail_url" :src="item.thumbnail_url" class="tw-w-full tw-h-full tw-object-cover" alt="" />
          <icon v-else icon="solar:videocamera-record-bold-duotone" class="tw-text-2xl tw-text-gray-400" />
        </div>
        <div class="tw-min-w-0 tw-max-w-[280px]">
          <p class="tw-font-medium tw-text-gray-900 tw-line-clamp-1">{{ item.title }}</p>
          <p v-if="item.description" class="tw-text-xs tw-text-gray-400 tw-line-clamp-1">{{ item.description }}</p>
        </div>
      </div>
    </td>

    <!-- Source -->
    <td class="tw-px-5 tw-py-3 tw-text-sm">
      <span class="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-whitespace-nowrap"
        :class="item.source_type === 'upload' ? 'tw-bg-purple-100 tw-text-purple-700' : 'tw-bg-blue-100 tw-text-blue-700'">
        <icon :icon="item.source_type === 'upload' ? 'solar:cloud-upload-bold' : 'solar:link-bold'" />
        {{ item.source_type === 'upload' ? $t('videos.source-upload', 'Upload') : (item.provider || $t('videos.source-url', 'URL')) }}
      </span>
    </td>

    <!-- Categories -->
    <td class="tw-px-5 tw-py-3 tw-text-sm">
      <div class="tw-flex tw-flex-wrap tw-gap-1 tw-max-w-[220px]">
        <span v-for="c in item.categories" :key="c.id"
          class="tw-bg-gray-100 tw-text-gray-600 tw-rounded tw-text-xs tw-px-2 tw-py-0.5 tw-border tw-border-solid tw-border-gray-200">{{ c.name }}</span>
        <span v-if="!item.categories || !item.categories.length" class="tw-text-gray-300">—</span>
      </div>
    </td>

    <!-- Duration -->
    <td class="tw-px-5 tw-py-3 tw-text-sm tw-text-center tw-whitespace-nowrap">
      {{ item.duration || '—' }}
    </td>

    <!-- Views -->
    <td class="tw-px-5 tw-py-3 tw-text-xs tw-text-center">
      <p class="tw-bg-gray-100 tw-text-gray-600 tw-rounded tw-px-2 tw-py-0.5 tw-inline-flex tw-items-center tw-gap-1">
        <icon icon="solar:eye-linear" /> {{ item.views_count ?? 0 }}
      </p>
    </td>

    <!-- Status toggle -->
    <td class="tw-px-5 tw-py-3 tw-text-sm">
      <label class="tw-flex tw-justify-center tw-items-center tw-p-0.5">
        <input type="checkbox" :disabled="loading" v-model="is_active" class="tw-appearance-none tw-peer" />
        <span class="tw-cursor-pointer tw-w-10 tw-h-[22px] tw-flex tw-items-center tw-flex-shrink-0 tw-p-[4px] tw-bg-gray-100 tw-rounded-full after:!tw-w-4 after:!tw-h-4 after:tw-aspect-square after:tw-max-h-4 after:tw-bg-white after:tw-rounded-full after:tw-shadow-md peer-checked:tw-bg-primary-400 tw-duration-300 tw-ease-in-out after:tw-duration-300 peer-checked:after:tw-translate-x-4 rtl:peer-checked:after:-tw-translate-x-4 peer-disabled:tw-animate-pulse"></span>
      </label>
    </td>

    <!-- Created -->
    <td class="tw-px-5 tw-py-3 tw-text-sm tw-text-center tw-whitespace-nowrap">
      <span v-if="item.created_at">{{ createdAt }}</span>
      <span v-else>—</span>
    </td>

    <!-- Actions -->
    <td class="tw-px-5 tw-pr-0 tw-py-3">
      <VideoTableActions :item="item" />
    </td>
  </tr>
</template>

<script setup>
import { defineProps, toRef, computed, ref, watch } from 'vue';
import moment from 'moment';
import { useI18n } from 'vue-i18n';
import VideoTableActions from './VideoTableActions';
import videosApi from '@/api/videos';
import useAlert from '@/composables/useAlert';

const i18n = useI18n();
const props = defineProps(['item']);
const item = toRef(props, 'item');
const is_active = ref(item.value.status === 'active');
const loading = ref(false);

watch(is_active, () => toggleStatus());

const createdAt = computed(() => moment(item.value.created_at).locale(i18n.locale.value).format('ll'));

const toggleStatus = async () => {
    loading.value = true;
    try {
        const res = await videosApi.admin.toggleStatus(item.value.id);
        item.value.status = res.data?.status || (is_active.value ? 'active' : 'inactive');
        useAlert(i18n.t('videos.status-updated', 'Status updated'), 'success');
    } catch (error) {
        console.error('Error toggling status:', error);
        // revert the toggle on failure
        is_active.value = item.value.status === 'active';
        useAlert(i18n.t('errors.something-went-wrong', 'Something went wrong'), 'danger');
    } finally {
        loading.value = false;
    }
};
</script>

<style></style>
